import { useEffect, useRef } from 'react'
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'
import { getDb, isFirebaseConfigured } from '@services/firebase'

export interface FirestoreNotice {
  id: string
  title: string
  message: string
  type: string
}

interface Handlers {
  onNotice: (notice: FirestoreNotice) => void
  onNoticeDeleted: (noticeId: string) => void
}

/**
 * Firestore-backed realtime notifications — the SignalR notification-hub replacement for the bell.
 * Subscribes to users/{uid}/notifications and fires onNotice for newly-added docs and onNoticeDeleted
 * for removed ones. The initial snapshot is skipped so existing notices don't re-toast on mount.
 * No-op unless Firebase is configured. (The SignalR notification hub stays connected elsewhere for
 * live session voting, which is out of scope for this migration.)
 */
export function useFirestoreNotifications(userId: string | undefined, handlers: Handlers) {
  // Keep handlers in a ref so re-renders don't re-subscribe the snapshot listener.
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!isFirebaseConfigured || !userId) return
    const db = getDb()
    if (!db) return

    const q = query(
      collection(db, 'users', userId, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    )

    let primed = false
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!primed) {
          // First emission is the existing backlog — prime and skip so we only react to new notices.
          primed = true
          return
        }
        snapshot.docChanges().forEach((change) => {
          const data = change.doc.data()
          const id = (data.id as string) ?? change.doc.id
          if (change.type === 'added') {
            handlersRef.current.onNotice({
              id,
              title: (data.title as string) ?? '',
              message: (data.message as string) ?? '',
              type: (data.type as string) ?? '',
            })
          } else if (change.type === 'removed') {
            handlersRef.current.onNoticeDeleted(id)
          }
        })
      },
      (err) => {
        console.warn('[firebase] notifications snapshot error', err)
      }
    )

    return () => unsubscribe()
  }, [userId])
}
